import { CSSProperties, useState } from 'react';
import { Modal } from '@/components/molecules';
import { HighlightBadge } from '@/components/molecules/HighlightBadge';
import { useClickref } from '@/hooks/useClickref';
import { PlanWithHighlight } from '@/utils/highlights';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import { PlanType } from '@/config/api';
import './ComparatorCard.scss';
import { formatPrice } from '@/utils/formatPrice';

export interface ComparatorCardProps {
  plan: PlanWithHighlight;
  priceDuration?: string;
  style?: CSSProperties;
  position?: number;
  planType?: PlanType;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function ComparatorCard({ plan, priceDuration = '1-month', style, position, planType }: ComparatorCardProps) {
  const { getClickrefUrl, handleClickrefLink } = useClickref();
  const { operator } = plan;
  const { price: basePrice, crossed_price: crossedPrice, promo_code: promoCode } = plan.offer.price;
  // The API duplicates the BO field « Information particulière » in both paths; accept either.
  const note = plan.offer.price.note ?? plan.offer.price_note;
  const rawUrl = plan.offer.url_lemon;
  const ctaUrl = getClickrefUrl(rawUrl);

  const months = priceDuration === '12-months' ? 12 : priceDuration === '24-months' ? 24 : 1;
  const hasCrossedPrice = crossedPrice != null && crossedPrice !== '';

  let totalPrice: number;
  if (hasCrossedPrice && plan.offer.raw_offer_duration) {
    const promoMonths = parseFloat(plan.offer.raw_offer_duration) * 12;
    const remainingMonths = Math.max(0, months - promoMonths);
    const promoMonthsUsed = months - remainingMonths;
    totalPrice = (basePrice * promoMonthsUsed) + (parseFloat(crossedPrice) * remainingMonths);
  } else {
    totalPrice = basePrice * months;
  }
  const periodLabel = months === 1 ? '/mois' : `/${months} mois`;

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDetailsOpen(true);
  };

  return (
  <a
      href={ctaUrl}
      onClick={(e) => handleClickrefLink(e, rawUrl)}
      target="_blank"
      rel="noopener nofollow noreferrer"
      className="comparator-card-link"
      style={style}
      data-gtm-offer-type="comparator_link"
      data-gtm-bloc-type="card"
      data-gtm-offer-url={ctaUrl}
      data-gtm-seller={operator.slug}
      data-gtm-product-name={slugify(plan.name)}
      data-gtm-position-marchand={position}
      data-gtm-product-category={planType}
      data-gtm-zone="comparator_list"
      data-gtm-position={position}
  >
    {plan.highlight ? (
      <HighlightBadge type={plan.highlight} planType={planType} />
    ) : (
      <div className="highlight-badge-placeholder" aria-hidden="true" />
    )}
    <div className="comparator-card">
      <div className="comparator-card__content">
      <div className="comparator-card__header">
          <div className="comparator-card__operator">
            <span className="comparator-card__operator-name">{operator.networks?.[0]?.name ?? ''}</span>
            <div className="comparator-card__title font-bold">{decodeHtmlEntities(plan.name)}</div>
          </div>

            <img
              src={operator.logo}
              alt={operator.name}
              className="comparator-card__operator-logo"
              width={49}
              height={52}
            />
      </div>

      <hr />

      <div className="comparator-card__prices">
          <div className="comparator-card__price">
            <div className="comparator-card__price-main">
              <span className="comparator-card__price-value font-bold">{formatPrice(totalPrice)}€</span>
              <span className="comparator-card__price-period">{periodLabel}</span>
            </div>
            {(crossedPrice || promoCode || note) && (
              <div className="comparator-card__price-extras">
                {crossedPrice && (
                  <span className="comparator-card__price-crossed">{formatPrice(crossedPrice)}€/mois</span>
                )}
                {promoCode && (
                  <span className="comparator-card__price-promo">{promoCode}</span>
                )}
                {note && (
                  <span className="comparator-card__price-note">{decodeHtmlEntities(note)}</span>
                )}
              </div>
            )}
          </div>
      </div>

        <div className="comparator-card__cta-wrapper">
          <button
            type="button"
            className="comparator-card__details-btn"
            onClick={handleDetailsClick}
          >
            Détails
          </button>
          <span className="comparator-card__cta">
            Voir l&apos;offre
          </span>
        </div>

      <ul className="comparator-card__features">
        {plan.visible_infos.map((info, index) => (
          <li key={index} className="comparator-card__feature" dangerouslySetInnerHTML={{ __html: info }} />
        ))}
      </ul>
      </div>
    </div>

    <Modal isOpen={isDetailsOpen} onClose={() => setIsDetailsOpen(false)}>
      <span className="comparator-card__modal-operator">{operator.networks?.[0]?.name ?? ''}</span>
      <div className="comparator-card__modal-title">{decodeHtmlEntities(plan.name)}</div>
      <ul className="comparator-card__modal-features">
        {plan.visible_infos.map((info, index) => (
          <li key={index} dangerouslySetInnerHTML={{ __html: info }} />
        ))}
      </ul>
      <a
        href={ctaUrl}
        onClick={(e) => handleClickrefLink(e, rawUrl)}
        target="_blank"
        rel="noopener nofollow noreferrer"
        className="comparator-card__modal-cta"
      >
        Voir l&apos;offre
      </a>
    </Modal>
  </a>
  );
}

export default ComparatorCard;
